import type { ObItemWithSubmission } from "./onboarding-api";
import { evaluateOnboardingVisibility } from "./onboarding-branching";

export interface AttentionItem {
    itemName: string;
    type: "pending" | "rejected" | "failed" | "waiting_on_you";
    /** Only on `waiting_on_you`: what she has to do, in her words. */
    detail?: string;
}

/**
 * Is this a selfie that cannot be checked until she uploads her photo ID?
 *
 * The face check compares the selfie against the ID, so with no ID there is
 * nothing to compare against and the selfie sits at `submitted` forever. The
 * server says so plainly - "Both selfie and photo ID must be uploaded" - but
 * she never sees that, and a `submitted` row reads as "we are working on it".
 *
 * Eleven applicants were in exactly that state on 22 Sep 2026, every one of
 * them with zero uploads against Identity Verification. Rahni Macon had been
 * waiting twenty days. Four of them had already been messaged about it by hand
 * and still did not act, which is the argument for the app saying it rather
 * than a person.
 *
 * Paired the way `prepareIdentityVerification` pairs them on the server: the
 * selfie is the `aws_face` item, the ID is the OTHER `document_upload` in the
 * same subsection. Matching on anything looser would pick up the legal
 * name-change upload, which also lives in Identity & Licensure.
 */
function isWaitingOnPhotoId(item: ObItemWithSubmission, all: ObItemWithSubmission[]): boolean {
    if (item.aiConfig?.verificationMethod !== "aws_face") {
        return false;
    }
    const photoId = all.find(
        (other) =>
            other.id !== item.id &&
            other.subsectionId === item.subsectionId &&
            other.itemType === "document_upload" &&
            other.aiConfig?.verificationMethod === "llm"
    );
    //  Not in this list (the caller passed one subsection, or the config
    //  changed): say nothing rather than guess.
    if (!photoId) {
        return false;
    }
    return (photoId.submission?.uploads?.length ?? 0) === 0;
}

/**
 * Items the nurse actually needs to do something about.
 *
 * Branching-aware, and it has to be. A submission row keeps whatever status it
 * last had even after her answers make that item irrelevant, so reading raw
 * status alone reports work she cannot do and must not do.
 *
 * The case that surfaced this: a nurse answers "no" to having had the Hepatitis
 * B vaccine, which branches the vaccine-card upload away and asks for a
 * declination instead. Her earlier rejected upload still sits at `rejected`, so
 * the app showed "Hepatitis B vaccine — REJECTED" for a document she is no
 * longer being asked for, on a screen offering no way to clear it. Admin renders
 * the same row correctly as "N/A · Not applicable", so the two screens told the
 * nurse and her reviewer different stories about one record.
 *
 * `not_required` is skipped for the same reason: it is the server saying
 * "branched off", not a task.
 */
export function getAttentionItems(items: ObItemWithSubmission[]): AttentionItem[] {
    // Same answer map the rest of the mobile branching uses: current answer
    // keyed by item id.
    const answers: Record<string, string> = {};
    for (const item of items) {
        const value = item.submission?.answerValue;
        if (value) {
            answers[item.id] = value;
        }
    }
    const visible = evaluateOnboardingVisibility(items, answers);

    const result: AttentionItem[] = [];
    for (const item of items) {
        const status = item.submission?.status;
        if (!status || status === "not_required") {
            continue;
        }
        // Hidden by branching → not her problem, whatever the row still says.
        if (!visible.has(item.id)) {
            continue;
        }

        if (status === "submitted" || status === "ai_verifying") {
            //  "Pending" means we are working on it. When the face check is
            //  waiting on her photo ID, nobody is working on anything.
            if (isWaitingOnPhotoId(item, items)) {
                result.push({
                    itemName: item.shortLabel ?? item.name,
                    type: "waiting_on_you",
                    detail: "Upload your photo ID and this will check itself automatically."
                });
                continue;
            }
            result.push({ itemName: item.shortLabel ?? item.name, type: "pending" });
        } else if (status === "rejected") {
            result.push({ itemName: item.shortLabel ?? item.name, type: "rejected" });
        } else if (status === "verification_failed") {
            result.push({ itemName: item.shortLabel ?? item.name, type: "failed" });
        }
    }
    return result;
}
